import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban,
  Award,
  Sparkles,
  ArrowRight,
  Layers,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { fetchStemProjects } from "../redux/stemThunk";

export default function StemDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { projects = [], status, error } = useSelector(
    (state) => state.stem || {}
  );

  useEffect(() => {
    // Call listActiveByCategory passing categoryId = 1
    dispatch(fetchStemProjects(1));
  }, [dispatch]);

  const stats = [
    {
      label: "STEM Projects",
      value: String(projects.length),
      icon: FolderKanban,
      color: "var(--brand)",
    },
    {
      label: "Active Stages",
      value: String(
        projects.reduce(
          (acc, p) => acc + (p.stages?.length || p._count?.stages || 0),
          0
        )
      ),
      icon: Layers,
      color: "var(--blue-2)",
    },
    {
      label: "Earned Badges",
      value: "5",
      icon: Award,
      color: "#f59e0b",
    },
    {
      label: "Exploration Points",
      value: "420",
      icon: Sparkles,
      color: "#8b5cf6",
    },
  ];

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Welcome Banner */}
      <div
        style={{
          background:
            "linear-gradient(135deg, var(--blue) 0%, var(--blue-2) 100%)",
          borderRadius: "14px",
          padding: "28px 32px",
          color: "var(--white)",
          marginBottom: "24px",
          boxShadow: "0 4px 14px rgba(37, 99, 235, 0.2)",
        }}
      >
        <span
          style={{
            fontSize: "12px",
            fontWeight: "700",
            letterSpacing: "1px",
            textTransform: "uppercase",
            background: "rgba(255, 255, 255, 0.2)",
            padding: "4px 10px",
            borderRadius: "20px",
            display: "inline-block",
            marginBottom: "10px",
          }}
        >
          STEM Learning Space
        </span>
        <h2 style={{ fontSize: "24px", fontWeight: "700", margin: "0 0 8px 0" }}>
          Welcome to your STEM Exploration Hub!
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "rgba(255,255,255,0.9)",
            maxWidth: "600px",
            lineHeight: "1.5",
          }}
        >
          Engage in hands-on science, technology, engineering, and mathematics
          projects. Build models, perform virtual simulations, and track your
          achievements.
        </p>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginBottom: "28px",
        }}
      >
        {stats.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              style={{
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "12px",
                padding: "18px 20px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  background: `color-mix(in srgb, ${item.color} 8%, white)`,
                  color: item.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={22} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "var(--ink)",
                  }}
                >
                  {item.value}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--mute)",
                    fontWeight: "500",
                  }}
                >
                  {item.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Projects List Section */}
      <div
        style={{
          background: "var(--white)",
          borderRadius: "12px",
          border: "1px solid var(--line)",
          padding: "20px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "var(--ink)",
                margin: "0 0 4px 0",
              }}
            >
              Active STEM Projects (Category 1)
            </h3>
            <span style={{ fontSize: "12px", color: "var(--mute)" }}>
              Fetched live from Stage Utility API
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              onClick={() => dispatch(fetchStemProjects(1))}
              disabled={status === "loading"}
              style={{
                background: "var(--bg)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "6px 10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "var(--mute)",
              }}
              title="Refresh projects"
            >
              <RefreshCw
                size={13}
                style={{
                  animation:
                    status === "loading"
                      ? "spin 1s linear infinite"
                      : "none",
                }}
              />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/student-stem/projects")}
              style={{
                background: "none",
                border: "none",
                color: "var(--brand)",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span>View All</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: "8px",
              background: "var(--red-soft)",
              border: "1px solid var(--red-line)",
              color: "var(--red-ink)",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px",
            }}
          >
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {status === "loading" && projects.length === 0 && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--mute)",
              fontSize: "14px",
            }}
          >
            <RefreshCw
              size={20}
              color="var(--brand)"
              style={{
                margin: "0 auto 8px auto",
                display: "block",
                animation: "spin 1s linear infinite",
              }}
            />
            <span>Loading STEM projects...</span>
          </div>
        )}

        {/* Projects List */}
        {projects.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {projects.map((p, idx) => (
              <div
                key={p.id || idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  background: "var(--bg)",
                  border: "1px solid var(--bg)",
                  transition: "all 0.15s ease",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "var(--ink)",
                      marginBottom: "4px",
                    }}
                  >
                    {p.name}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      fontSize: "12px",
                      color: "var(--mute)",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        background: "var(--blue-soft)",
                        color: "var(--blue)",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontWeight: "600",
                        fontSize: "11px",
                      }}
                    >
                      {p.category?.name || "STEM"}
                    </span>
                    {p.description && (
                      <>
                        <span>•</span>
                        <span
                          style={{
                            maxWidth: "400px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {p.description}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--mute)",
                      background: "var(--line)",
                      padding: "3px 9px",
                      borderRadius: "12px",
                      fontWeight: "500",
                    }}
                  >
                    {p.stages?.length ?? p._count?.stages ?? 0} Stages
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color:
                        p.status === "ACTIVE" ? "var(--green-ink)" : "var(--mute)",
                      background:
                        p.status === "ACTIVE" ? "var(--green-soft)" : "var(--bg)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                    }}
                  >
                    {p.status || "ACTIVE"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          status !== "loading" && (
            <div
              style={{
                textAlign: "center",
                padding: "36px 20px",
                color: "var(--mute)",
              }}
            >
              <FolderKanban
                size={36}
                color="var(--line)"
                style={{ margin: "0 auto 8px auto", display: "block" }}
              />
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  margin: "0 0 4px 0",
                  color: "var(--ink)",
                }}
              >
                No active STEM projects found
              </p>
              <span style={{ fontSize: "12.5px" }}>
                Active projects added under category 1 will appear here.
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
}
